import { Link, Outlet, useLocation } from "react-router";

import { areaAllowsRoles, areaForPath, isAreaOpenable } from "../navigation/offering";
import { useOffering } from "../navigation/use-offering";
import { AsyncState } from "../ui/async-state";
import { Button } from "../ui/button";

/**
 * The one guard of the staff shell. It sits above every route of the shell and asks the offering (`navigation/offering.ts`) whether the
 * area that owns the address opens for this staff member. A staff member who types the address of an area their role does not have (the
 * catalog editor on `/staff`, the administrator on `/catalog`) sees a refusal, not the page. An area behind a feature (Payments) also
 * needs the API to say the feature is on in this environment: while it has not answered the guard waits, and when it says no (or does not
 * answer) the area is "not available here". An address that no area owns is refused too, so a route cannot be added without its area.
 * This is the console's own presentation rule (ADR 0012): the API refuses the requests again for a role without the capability.
 */
export function AreaGuard() {
  const { pathname } = useLocation();
  const offering = useOffering();
  const area = areaForPath(pathname);
  if (offering.rolesLoading) {
    return (
      <AsyncState
        kind="loading"
        title="Checking your access"
        message="Reading your staff role."
        headingLevel={1}
      />
    );
  }
  if (area === undefined)
    return <Forbidden message="This address is not an area of the console." />;
  const key = area.key;
  if (!areaAllowsRoles(key, offering.roles))
    return <Forbidden message="Your staff role does not include this area." />;
  if (area.feature !== undefined && offering.featuresLoading) {
    return (
      <AsyncState
        kind="loading"
        title="Checking this area"
        message="Reading what is switched on."
        headingLevel={1}
      />
    );
  }
  if (!isAreaOpenable(key, offering)) {
    return (
      <Forbidden
        title="Not available here"
        message="This area is not switched on in this environment."
      />
    );
  }
  return <Outlet />;
}

/** The refusal screen: what happened, why, and the way out. It names no resource and holds no data. */
function Forbidden({
  title = "Not available for your role",
  message,
}: Readonly<{ title?: string; message: string }>) {
  return (
    <AsyncState
      kind="error"
      title={title}
      message={message}
      headingLevel={1}
      accessory={
        <Button asChild variant="outline" className="justify-self-start">
          <Link to="/home">Go to Home</Link>
        </Button>
      }
    />
  );
}
